<template>
  <div class="your-profile-component generic-component">
    <div v-if="product">
      <your-profile-hero-component :heading="heading" :description="description"></your-profile-hero-component>

      <template v-if="profile && currentScheme">
        <your-profile-form-component
          :session="session"
          :adminPortalData="adminPortalData"
          :product="product"
          :profile="profile"
          :contactTheTeam="contactTheTeam"
          :employeeSectionHeading="employeeSectionHeading"
          :schemeDescription="schemeDescription"
          :schemeSectionHeading="schemeSectionHeading"
          :taxBandTooltip="taxBandTooltip"
          :scheme="currentScheme"
        />
      </template>
      <template v-else>
        <div class="text-center mt-4">
          <LoadingIcon/>
        </div>
      </template>

      <ChargingProfile :adminPortalData="adminPortalData"/>

    </div>
    <footer-text v-if="footerText" :footerText="footerText"/>
  </div>

</template>

<script>
import {EventBus} from '@/event-bus.js';
import FooterText from "@/components/footer/footer-text.vue";
import YourProfileFormComponent from "@/components/your-profile-legacy/your-profile-form-component.vue";
import YourProfileHeroComponent from "@/components/your-profile-legacy/your-profile-hero-component.vue";
import ChargingProfile from "@/components/charging-profile-component.vue";
import LoadingIcon from "@/components/loading-icon.vue";
import {useRoute} from "vue-router";

export default {
  components: {LoadingIcon, ChargingProfile, FooterText, YourProfileFormComponent, YourProfileHeroComponent},

  props: [
    'session',
    'adminPortalData',
    'adminPortalPrimaryProduct'
  ],

  data: () => {
    return {
      profile: null,
      driverGrades: null,
    }
  },

  setup() {
    const route = useRoute();
    return {route}
  },

  async beforeMount(to, from, next) {
    this.vehicles = [];

    EventBus.$emit('fetchData', ['driverprofile', 'drivergrades']);

    this.type = this.$route.meta.type;
  },

  mounted() {
    this.get();
    EventBus.$on('reload-driver-profile', () => {
      this.get('Loading');
    });
  },

  computed: {
    product() {
      return this.adminPortalData?.content?.products?.[this.currentScheme][0];
    },
    currentScheme() {
      return this.route.params.scheme;
    },
    contactTheTeam() {
      return this.product.your_profile_contact_the_team ?? this.adminPortalPrimaryProduct.your_profile_request_a_change_link_text;
    },
    description() {
      return this.product.your_profile_description;
    },
    employeeSectionHeading() {
      return this.product.your_profile_employee_section_heading;
    },
    heading() {
      return this.product.your_profile_heading;
    },
    schemeDescription() {
      return this.product.your_profile_scheme_description;
    },
    schemeSectionHeading() {
      return this.product.your_profile_scheme_section_heading;
    },
    taxBandTooltip() {
      return this.currentScheme === 'ecoamaps' ? 'This is your Income Tax band as provided based on your basic salary.' : this.adminPortalPrimaryProduct.your_profile_tax_band_tooltip;
    },
    footerText() {
      return this.adminPortalData.content?.products?.ecoamaps
      && this.adminPortalData.content?.products?.ecoamaps[0]?.fca_requirement_and_content_fca_description_required
      && this.adminPortalData.content?.products?.ecoamaps[0]?.fca_requirement_and_content_fca_description
      && this.adminPortalData.content?.products?.ecoamaps[0]?.fca_description_required_on_your_profile
        ? this.adminPortalData.content.products.ecoamaps[0].fca_requirement_and_content_fca_description
        : false;
    }
  },

  methods: {
    get(customLoadingTitle) {
      const loadingTitle = customLoadingTitle ?? 'Fetching Driver Profile';

      EventBus.$emit('loadingTitle', loadingTitle);
      EventBus.$emit('loading', true);

      this.$http.get('/api/your-profile/' + this.currentScheme)
        .then(response => {
          this.$root.session = response.data.session;
          const details = response.data.data;
          this.prepareDriverProfileData(details);

          if (response.data.redirect) {
            this.$router.push({name: response.data.redirect, params: {...this.$route.params}});
          }
        })
        .catch(err => {
          let response = err.response;
          console.log(err);

          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: response.message
          });

          this.error = err.message.toString();
          this.$router.push({name: response.redirect, params: {...this.$route.params}});
        }).finally(() => {
        EventBus.$emit('loading', false);
      });
    },

    prepareDriverProfileData(details) {
      const profile = {};
      profile.fullName = details.contacts[0].fullName;
      profile.forename = details.firstName;
      profile.surname = details.surname;
      profile.emailAddress = details.contacts[0].emailAddress;
      profile.mobile = details.contacts[0].mobile;
      profile.homeAddress = details.homeAddress ? details.homeAddress.replace(/(?:\r\n|\r|\n)/g, ', ') : '';
      profile.dateOfBirth = details.dateOfBirth;

      const n = profile.homeAddress.lastIndexOf(',');
      profile.postcode = profile.homeAddress.substring(n + 1)
      profile.homeAddress = profile.homeAddress.substring(0, n);

      profile.incomeTaxBand = details.incomeTaxBand.ratePercent;
      profile.companyCarGrade = details.driverGrades[0].name;
      profile.privateCarGrade = details.driverGrades.map(grade => grade.name).join(", ");
      profile.privateCarCashAllowance = details.currentCashAllowances.totalMonthlyCurrentAllowance;
      profile.totalAnnualMileage = Number(details.annualBusinessDistance) + Number(details.annualPrivateDistance);
      profile.businessAnnualMileage = Number(details.annualBusinessDistance);
      profile.privateAnnualMileage = Number(details.annualPrivateDistance);
      profile.driverLicence = details.driverLicence;
      profile.insuranceQuestions = details.insuranceQuestions;
      profile.additionalDrivers = details.additionalDrivers;
      profile.mainDriver = details.mainDriver;

      if (profile.additionalDrivers) {
        profile.additionalDrivers = profile.additionalDrivers.map(additionalDriver => {
          return {
            id: additionalDriver.id,
            fullName: additionalDriver.first_name + ' ' + additionalDriver.last_name,
            forename: additionalDriver.first_name,
            surname: additionalDriver.last_name,
            postcode: additionalDriver.postcode,
            emailAddress: additionalDriver.email,
            dateOfBirth: additionalDriver.date_of_birth,
            mainDriver: additionalDriver.main_driver,
            driverLicence: {licenceNumber: additionalDriver.driving_license},
            insuranceQuestions: additionalDriver.insurance_questions,
          }
        });
      } else {
        profile.additionalDrivers = [];
      }

      profile.primaryOvernightVehicleLocation = details.primaryOvernightVehicleLocation;
      profile.acceptanceOfSalarySacrificeConditionsOfUseAndInsurance = details.acceptanceOfSalarySacrificeConditionsOfUseAndInsurance;

      this.profile = profile;

      setTimeout(() => {
        EventBus.$emit('yourProfileCompleted', details.profileCompleted);

        if (!details.profileCompleted && (this.adminPortalData.scheme !== 'ECO / AMAPS')) {
          EventBus.$emit('editProfile');
        }
      }, 500)

    }
  }
}
</script>
